import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import hubLayout from 'styles/css/hubLayout.module.css';
import { Recorder, SubRecorder, SubUser } from 'type/type';
import { useModal } from 'components/common/ModalLayout';
import { deleteSubUser, encryptSHA256, getIdDuplicateCheck, getRecorderList, getSubUserByIdx, upsertSubUser } from 'api/api';
import { UserLoginInfoContext } from 'pages/Layout';
import { getCurrentDateTime } from 'pages/utils/commonUtils';
import Loading from 'components/common/Loading';
import { PwCheckInputField, PwInputField } from 'components/common/InputField';
import { styled } from 'styled-components';
import { logout } from 'api/bridgeUser';

const ToggleWrapper = styled.div<{ $isOn: boolean }>`
  width: 90px;
  height: 30px;
  background-color: ${({ $isOn }) => ($isOn ? '#1976d2' : '#e0e0e0')};
  border-radius: 20px;
  border: 2px solid ${({ $isOn }) => ($isOn ? '#1976d2' : '#e0e0e0')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  position: relative;
  font-weight: bold;
  font-size: 14px;
  color: ${({ $isOn }) => ($isOn ? 'white' : '#999')};
  transition: background-color 0.3s;

  .label {
    z-index: 2;
  }

  .switch-handle {
    width: ${({ $isOn }) => ($isOn ? '38px' : '42px')};
    height: 100%;
    background-color: white;
    border-radius: 18px;
    position: absolute;
    top: 0;
    left: 0;
    transform: ${({ $isOn }) => ($isOn ? 'translateX(48px)' : 'translateX(0)')};
    transition: transform 0.3s;
  }
`;

function SubUserProcess() {
  const { userLoginInfo } = useContext(UserLoginInfoContext);
  const navigate = useNavigate();
  const { showModal } = useModal();
  const { subUserIdx } = useParams();
  const [loading, setLoading] = useState(false);
  const [isChangePw, setIsChangePw] = useState<boolean>(false);
  const [isPushOn, setIsPushOn] = useState(true);
  const [isDuplicateCheck, setIsDuplicateCheck] = useState(subUserIdx ? true : false);
  const pushToggle = () => {
    setSubRecorderList((prevData) =>
      prevData.map(item => ({
        ...item,
        push: {all: isPushOn ? 0 : 1},
      }))
    );
    setIsPushOn(!isPushOn);
  };
  const liveToggle = () => {
    setSubUser((prevData) => ({
      ...prevData,
      auth_live: !prevData.auth_live,
    }));
  };
  const guardChangeToggle = () => {
    setSubUser((prevData) => ({
      ...prevData,
      auth_guard_change: !prevData.auth_guard_change,
    }));
  };
  const searchToggle = () => {
    setSubUser((prevData) => ({
      ...prevData,
      auth_search: !prevData.auth_search,
    }));
  };

  // useForm
  const { register, formState: { errors, isValid }, reset, trigger, watch } = useForm<any>({
    mode: 'all',
  });
  const passwordWatch = watch("password");

  const currentYYMMDD = getCurrentDateTime();
  const [subUser, setSubUser] = useState<SubUser>({
    idx: subUserIdx ? Number(subUserIdx) : 0,
    user_id: userLoginInfo ? userLoginInfo.id : 'x',
    id: '',
    name: '',
    limit: 0,
    access_day: 2,
    access_start_hour: 0,
    access_end_hour: 24,
    auth_live: true,
    auth_search: true,
    auth_ptz: true,
    auth_preset: true,
    auth_display_quality: true,
    auth_guard_change: true,
    os_type: '',
    os_ver: '',
    app_ver: '',
    phone_model: '',
    push_token: '',
    create_date: currentYYMMDD,
    update_date: currentYYMMDD,
    
    recorder_list: undefined,
  });
  const [subRecorderList, setSubRecorderList] = useState<SubRecorder[]|[]>([]);
  const [recorderList, setRecorderList] = useState<Recorder[]|[]>([]);

  const getSubUser = useCallback(async () => {
    if (!subUserIdx) return;
    try {
      const subUserRes = await getSubUserByIdx(Number(subUserIdx));
      if (subUserRes.code === 200) {
        setSubUser(subUserRes.data);
        setSubRecorderList(subUserRes.data.recorder_list);
        if (subUserRes.data.recorder_list.length > 0) {
          setIsPushOn(subUserRes.data.recorder_list[0].push.all === 1);
        }
        reset(subUserRes.data);
      } else {
        showModal("alert", subUserRes.message);
      }
    } catch (error) {
      console.log(error);
    }
  }, [subUserIdx, reset]);

  const getRecorder = useCallback(async () => {
    if (!userLoginInfo) {
      return  showModal("alert", '잘못된 접근입니다.', { confirm: true, cancel: true },
        logout,
        logout
      );
    }
    try {
      const recorderRes = await getRecorderList(0, 0, { user_id: userLoginInfo.id });
      if (recorderRes.code === 200) {
        setRecorderList(recorderRes.data);
      } else {
        showModal("alert", recorderRes.message, { confirm: true, cancel: true },
        () => navigate(-2),
        () => navigate(-2)
        );
      }
    } catch (error) {
      console.log(error);
    }
  }, [userLoginInfo, navigate]);

  const _deleteSubUser = async () => {
    try {
      showModal('confirm', `부계정을 삭제하시겠습니까?`, { confirm: false, cancel: false },
        async () => {
          const subUserRes = await deleteSubUser(subUser.idx);
          if (subUserRes.code === 200) {
            showModal('alert', `부계정 삭제가 완료되었습니다.`, { confirm: true, cancel: true },
              () => navigate(-2),
              () => navigate(-2)
            );
          } else {
            showModal('alert', subUserRes.message);
          }
        }
      );
    } catch (error) {
      console.error(error);
    }
  }

  const _upsertSubUser = async () => {
    if (!isValid) {
      await trigger();
      return showModal('alert', '모든 정보를 입력해 주세요.');
    }
  
    if (subRecorderList.length === 0) {
      return showModal('alert', '녹화기는 1대 이상 선택해주세요.');
    }
  
    if (!isDuplicateCheck) {
      return showModal('alert', 'ID 중복체크를 해주세요.');
    }
  
    let encryptedPassword;
    const originalPassword = subUser.password;
    if (subUserIdx) {
      encryptedPassword = originalPassword ? encryptSHA256(originalPassword) : undefined;
    } else {
      encryptedPassword = encryptSHA256(subUser.id);
    }
  
    const requestData = {
      ...subUser,
      password: encryptedPassword,
      recorder_list: subRecorderList,
    };
    try {
      const subUserRes = await upsertSubUser(requestData);
  
      switch (subUserRes.code) {
        case 200:
          showModal( 'alert', `${subUserIdx ? '수정' : '등록'}이 완료되었습니다.`, { confirm: true, cancel: true },
            () => navigate(-2),
            () => navigate(-2)
          );
          break;
  
        case 411:
          showModal('alert', '이미 사용중인 아이디입니다. 다시 입력해주세요.');
          break;
  
        default:
          showModal('alert', subUserRes.message);
          break;
      }
    } catch (error) {
      console.error(error);
    } finally {
      // 되돌릴 필요가 있는 경우만 패스워드 복원
      subUser.password = originalPassword;
    }
  };

  const duplicateCheck = async () => {
    try {
      if (!subUser.id) {
        return showModal('alert', 'ID를 입력해주세요.');
      }
      const response = await getIdDuplicateCheck(subUser.id);
      if (response.code === 200) {
        showModal('alert', `'${subUser.id}'는 사용가능한 아이디입니다.`);
        setIsDuplicateCheck(true);
      } else {
        showModal('alert', `이미 사용중인 아이디입니다. 다시 입력해주세요.`);
        setIsDuplicateCheck(false);
      }
    } catch (error) {
      console.log(error);
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
  
    const numericFields = ['access_day', 'access_start_hour', 'access_end_hour', 'limit'];
    const numericValue = Number(value);
    const lowercaseValue = value.toLowerCase();
  
    // 시간 유효성 검사
    if (
      (name === 'access_start_hour' && numericValue > subUser.access_end_hour) ||
      (name === 'access_end_hour' && numericValue < subUser.access_start_hour)
    ) {
      return showModal('alert', '접속 가능 시작 시간이 종료시간보다 클 수 없습니다.');
    }
  
    // 업데이트할 값 결정
    let updatedValue: string|number;
    if (name === 'id') {
      setIsDuplicateCheck(false);
      updatedValue = lowercaseValue;
    } else if (numericFields.includes(name)) {
      updatedValue = numericValue;
    } else {
      updatedValue = value;
    }
  
    // subUser 상태 업데이트
    setSubUser((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));
  
    // ID 변경 시 subRecorderList 전체에 반영
    if (name === 'id') {
      setSubRecorderList((prevList) =>
        prevList.map((item) => ({
          ...item,
          id: lowercaseValue,
        }))
      );
    }
  };

  const subRecorderHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.target;
    const selected = recorderList[Number(value)];
  
    setSubRecorderList((prevData) => {
      if (checked) {
        const isExist = prevData.some(item => item.recorder_idx === selected.idx);
        if (!isExist) {
          const newItem: SubRecorder = {
            idx: 0,
            user_id: userLoginInfo ? userLoginInfo.id : 'x',
            id: subUser.id,
            recorder_idx: selected.idx,
            recorder_mac: selected.mac,
            push: { all: isPushOn ? 1 : 0 },
            create_date: currentYYMMDD,
          };
          return [...prevData, newItem];
        }
        return prevData;
      } else {
        return prevData.filter(item => item.recorder_idx !== selected.idx);
      }
    });
  };

  const mainApi = useCallback(async () => {
    try {
      setLoading(true);
      await getSubUser();
      await getRecorder();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
    
  }, [getSubUser, getRecorder]);

  useEffect(() => {
    mainApi();
  }, [mainApi]);
  useEffect(() => {
    const handleResize = () => {
      window.scrollTo(0, document.body.scrollHeight);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const isFirstRender = useRef(0);
  useEffect(() => {
    if (isFirstRender.current < 2) {
      isFirstRender.current = isFirstRender.current + 1;
      return; // 첫 렌더링에서는 트리거 안 함
    }
    // password 값이 바뀌면 passwordcheck의 유효성 재검사
    trigger("passwordcheck");
  }, [passwordWatch, trigger]);
  return (
    <>
      {loading ? <Loading /> :
        <div className={`${hubLayout.cnt_box}`} >
          <div className={`${hubLayout.acc__cnt}`}>
            <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`}>1. 사용자 정보</h2>
            <ul className={`${hubLayout.inp_list}`}>
              <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
                <label className={`${hubLayout.imp}`}>이름</label>
                <input type='text' placeholder='한글, 영문, 숫자 2~20자' className={`${errors.name && hubLayout.border_red}`} value={subUser.name}
                  {...register(`name`, {
                    required: true,
                    pattern: /^[가-힣a-zA-Z0-9]{1,19}$/g,
                    onChange: handleChange
                  })}
                />
                <p className={`${errors.name && hubLayout.tx_red}`}>* 이름은 2~20자의 한글과 영문, 숫자로 입력 가능합니다.</p>
              </li>
              <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
                <label className={`${hubLayout.imp}`}>ID</label>
                <div className={`${hubLayout.display_flex}`}>
                  <input type='text' placeholder='영문, 숫자 4~20자' className={`${errors.id && hubLayout.border_red}`} value={subUser.id} readOnly={subUserIdx ? true : false}
                    {...register(`id`, {
                      required: true,
                      pattern: /^[a-z][0-9a-z]{3,19}$/g,
                      onChange: handleChange
                    })}
                  />
                  {!subUserIdx &&
                    <button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue}`} style={{ width: "100px" }} onClick={duplicateCheck}>중복체크</button>
                  }
                </div>
                <p className={`${errors.id && hubLayout.tx_red}`}>* 아이디는 영문 소문자로 시작하며, 4~20자의 영문 소문자와 숫자로만 입력 가능합니다.</p>
              </li>
              {subUserIdx &&
                <div className={`${hubLayout.btn_group} ${hubLayout.back_white}`} style={{ padding: "0" }}>
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`}
                    onClick={async () => {setIsChangePw((prev) => !prev); await trigger();}}>
                    {isChangePw ? '비밀번호 변경 취소' : '비밀번호 변경'}
                  </button>
                </div>
              }
              {(isChangePw && subUserIdx) &&
                <>
                  <PwInputField value={subUser} register={register} registerOptions={{ required: true, onChange: handleChange }} errors={errors} />
                  <PwCheckInputField value={subUser} register={register} registerOptions={{ required: true, onChange: handleChange }} errors={errors} />
                </>
              }
            </ul>
            <ul className={`${hubLayout.inp_list}`}>
              <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`}>2. 권한 정보</h2>
              <li className={`${hubLayout.inp_set} ${hubLayout.label_text}`}>
                <div className={`${hubLayout.acc_item_info}`}>
                  <div className={`${hubLayout.acc_item_top}`}>
                    <span className={`${hubLayout.id}`}>푸시 알림을 함께 보내요.</span>
                    <ToggleWrapper $isOn={isPushOn} onClick={pushToggle}>
                      <span className="label">{isPushOn ? 'ON' : 'OFF'}</span>
                      <div className="switch-handle" />
                    </ToggleWrapper>
                  </div>
                  <div className={`${hubLayout.acc_item_top}`}>
                    <span className={`${hubLayout.id}`}>경계 설정을 할 수 있어요.</span>
                    <ToggleWrapper $isOn={subUser.auth_guard_change} onClick={guardChangeToggle}>
                      <span className="label">{subUser.auth_guard_change ? 'ON' : 'OFF'}</span>
                      <div className="switch-handle" />
                    </ToggleWrapper>
                  </div>
                  <div className={`${hubLayout.acc_item_top}`}>
                    <span className={`${hubLayout.id}`}>실시간 영상을 볼 수 있어요.</span>
                    <ToggleWrapper $isOn={subUser.auth_live} onClick={liveToggle}>
                      <span className="label">{subUser.auth_live ? 'ON' : 'OFF'}</span>
                      <div className="switch-handle" />
                    </ToggleWrapper>
                  </div>
                  <div className={`${hubLayout.acc_item_top}`}>
                    <span className={`${hubLayout.id}`}>녹화 영상을 볼 수 있어요.</span>
                    <ToggleWrapper $isOn={subUser.auth_search} onClick={searchToggle}>
                      <span className="label">{subUser.auth_search ? 'ON' : 'OFF'}</span>
                      <div className="switch-handle" />
                    </ToggleWrapper>
                  </div>
                  <div className={`${hubLayout.acc_item_top}`}>
                    <span className={`${hubLayout.id}`}>최대 접속 유지 시간</span>
                    <span className={`${hubLayout.id}`} style={{ justifyItems: "center" }}>
                      <div className={`${hubLayout.inp_select}`} >
                        <select className={`${hubLayout.inp_select} ${errors.limit && hubLayout.border_red}`} value={subUser.limit}
                          {...register(`limit`, {
                            onChange: handleChange
                          })}
                        >
                        {Array.from({ length: 7 }, (_, idx) => (
                          <option key={`limit_${idx}`} value={idx * 10}>{idx === 0 ? '무제한'  : `${idx * 10}분`}</option>
                        ))}
                        </select>
                      </div>
                    </span>
                  </div>
                  <div className={`${hubLayout.acc_item_top}`}>
                    <span className={`${hubLayout.id}`}>접속 가능 요일</span>
                    <span className={`${hubLayout.id}`} style={{ justifyItems: "center" }}>
                      <div className={`${hubLayout.inp_select}`} >
                        <select className={`${hubLayout.inp_select} ${errors.access_day && hubLayout.border_red}`} value={subUser.access_day}
                          {...register(`access_day`, {
                            onChange: handleChange
                          })}
                        >
                        {Array.from({ length: 3 }, (_, idx) => (
                          <option key={`access_day_${idx}`} value={idx}>{idx === 0 ? '접속불가' : idx === 1 ? '평일' : '매일'}</option>
                        ))}
                        </select>
                      </div>
                    </span>
                  </div>
                  <div className={`${hubLayout.acc_item_top}`}>
                    <span className={`${hubLayout.id}`}>접속 가능 시간</span>
                  </div>
                  <div className={`${hubLayout.acc_item_top}`}>
                    <span className={`${hubLayout.id}`} style={{ display: "flex", justifyItems: "center", justifyContent: "space-between", alignItems: "center" }}>
                      <div className={`${hubLayout.inp_select}`} style={{ width: "45%" }}>
                        <select className={`${hubLayout.inp_select} ${errors.access_start_hour && hubLayout.border_red}`} value={subUser.access_start_hour}
                          {...register(`access_start_hour`, {
                            onChange: handleChange
                          })}
                        >
                        {Array.from({ length: 23 }, (_, idx) => (
                          <option key={`access_start_hour_${idx}`} value={idx}>{idx}시</option>
                        ))}
                        </select>
                      </div>
                      ~
                      <div className={`${hubLayout.inp_select}`} style={{ width: "45%" }}>
                        <select className={`${hubLayout.inp_select} ${errors.access_end_hour && hubLayout.border_red}`} value={subUser.access_end_hour}
                          {...register(`access_end_hour`, {
                            onChange: handleChange
                          })}
                        >
                        {Array.from({ length: 24 }, (_, idx) => (
                          <option key={`access_end_hour_${idx}`} value={idx + 1}>{idx + 1}시</option>
                        ))}
                        </select>
                      </div>
                    </span>
                  </div>
                </div>
              </li>
            </ul>
            <ul className={`${hubLayout.inp_list}`}>
              <h2 className={`${hubLayout.cnt_title} ${hubLayout.tx_blue}`}>2. 녹화기 선택</h2>
              {recorderList.map((recorder, idx) => 
                <li key={`recorder_${idx}`} className={`${hubLayout.acc_item} ${hubLayout.inp_set}`}>
                  <label htmlFor={`inp-array-${idx}`}>
                    <div className={`${hubLayout.acc_item_top}`} style={{ padding: '10px' }}>
                      <p className={`${hubLayout.id}`}>{recorder.reg_name}</p>
                      <span className={`${hubLayout.margin_right_10}`}>
                        <input type="checkbox" value={idx} id={`inp-array-${idx}`} className={`${hubLayout.check}`} checked={subRecorderList.some(sub => sub.recorder_idx === recorder.idx)}
                          {...register(`array_number_${idx}`, {
                            onChange: subRecorderHandleChange
                          })}
                        />
                      </span>
                    </div>
                  </label>
                </li>
              )}
            </ul>
            <div className={`${hubLayout.btn_group} ${hubLayout.fixed_bottom_title} ${hubLayout.back_white}`}>
              {subUserIdx &&
                <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.pink}`} onClick={_deleteSubUser}>삭제</button>
              }
              <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} onClick={_upsertSubUser}>{subUserIdx ? '수정' : '등록'}</button>
            </div>
          </div>
        </div>
      }
    </>
  )
}

export default SubUserProcess;